import { chartOptionsFactory } from '@/shared/ui/chart';

/**
 * Formats a number with "к" or "м" prefix if it's large.
 * @param {number} value - The number to format.
 * @returns {string} The formatted number string.
 */
function formatValueWithPrefix(value) {
  if (value >= 1000000) {
    return `${(value / 1000000).toFixed(1)}м`;
  } else if (value >= 1000) {
    return `${(value / 1000).toFixed(1)}к`;
  }
  return `${value}`;
}

/**
 * Determines the time period type based on the keys in the data.
 * @param {object} data - The backend data object.
 * @returns {'day' | 'week' | 'month'} The detected time period type.
 */
function detectPeriodType(data) {
  const keys = Object.keys(data);
  if (keys.some(key => key.includes('-'))) {
    if (keys.some(key => key.includes('W'))) {
      return 'week';
    } else if (keys.some(key => key.split('-').length === 3)) {
      return 'day';
    }
  }
  return 'month';
}

/**
 * Formats the x-axis label based on the period type.
 * @param {string} key - The data key (YYYY-MM, YYYY-MM-DD, or YYYY-WW).
 * @param {string} periodType - The period type ('day', 'week', or 'month').
 * @returns {string} The formatted label.
 */
function formatLabel(key, periodType) {
  const monthNames = {
    '01': 'січ', '02': 'лют', '03': 'бер', '04': 'кві', '05': 'тра', '06': 'чер',
    '07': 'лип', '08': 'сер', '09': 'вер', '10': 'жов', '11': 'лис', '12': 'гру'
  };

  switch (periodType) {
    case 'day': {
      const [year, month, day] = key.split('-');
      return `${day} ${monthNames[month]}`;
    }
    case 'week': {
      const [weekYear, weekNum] = key.split('-W');
      return `Тиждень ${weekNum}`;
    }
    case 'month':
    default: {
      const [mYear, mMonth] = key.split('-');
      return `${monthNames[mMonth]} ${mYear}`;
    }
  }
}

/**
 * Gets the current period key based on today's date.
 * @param {string} periodType - The period type ('day', 'week', or 'month').
 * @returns {string} The current period key.
 */
function getCurrentPeriodKey(periodType) {
  const now = new Date();
  const year = now.getFullYear();
  const month = (now.getMonth() + 1).toString().padStart(2, '0');
  const day = now.getDate().toString().padStart(2, '0');

  switch (periodType) {
    case 'day':
      return `${year}-${month}-${day}`;
    case 'week': {
      const oneJan = new Date(year, 0, 1);
      const weekNum = Math.ceil((((now - oneJan) / 86400000) + oneJan.getDay() + 1) / 7);
      return `${year}-W${weekNum.toString().padStart(2, '0')}`;
    }
    case 'month':
    default:
      return `${year}-${month}`;
  }
}

/**
 * Sorts period keys chronologically.
 * @param {string[]} keys - Array of period keys.
 * @param {string} periodType - The period type ('day', 'week', or 'month').
 * @returns {string[]} Sorted array of keys.
 */
function sortPeriodKeys(keys, periodType) {
  return keys.sort((a, b) => {
    if (periodType === 'day') {
      return new Date(a) - new Date(b);
    } else if (periodType === 'week') {
      const [aYear, aWeek] = a.split('-W');
      const [bYear, bWeek] = b.split('-W');
      return aYear - bYear || aWeek - bWeek;
    } else { // month
      const [aYear, aMonth] = a.split('-');
      const [bYear, bMonth] = b.split('-');
      return aYear - bYear || aMonth - bMonth;
    }
  });
}

/**
 * Calculates the chart options for order value or count data.
 * @param {object} backendData - The raw data from the backend.
 * @param {string} attribute - The attribute to display on the chart.
 * @param {string} type - The type of data being displayed ('currency' or 'count').
 * @returns {object} The chart options object generated by chartOptionsFactory.
 */
export function calculateOrdersChartOptions(backendData, attribute, type = 'currency') {
  const chartData = [
    {
      x: [],
      y: [],
      type: 'bar',
      marker: {
        color: [],
        pattern: {
          shape: [],
          fillmode: 'replace',
        }
      }
    }
  ];

  const colors = [];
  const patterns = [];
  const xLabels = [];
  const yValues = [];

  let minValue = Infinity;
  let maxValue = -Infinity;
  let periodsWithMinValues = [];

  const hasData = backendData && Object.keys(backendData).length > 0;

  if (hasData) {
    const periodType = detectPeriodType(backendData);
    const sortedKeys = sortPeriodKeys(Object.keys(backendData), periodType);

    sortedKeys.forEach(key => {
      const label = formatLabel(key, periodType);
      let totalValue = backendData[key][attribute];

      xLabels.push(label);
      yValues.push(totalValue);

      if (totalValue < minValue) {
        minValue = totalValue;
        periodsWithMinValues = [key];
      } else if (totalValue === minValue) {
        periodsWithMinValues.push(key);
      }

      if (totalValue > maxValue) {
        maxValue = totalValue;
      }
    });

    const currentPeriodKey = getCurrentPeriodKey(periodType);

    sortedKeys.forEach(key => {
      if (key === currentPeriodKey) {
        colors.push('rgba(251, 146, 60, 1)');
        patterns.push('');
      } else if (periodsWithMinValues.includes(key)) {
        colors.push('rgba(254, 71, 34, 1)');
        patterns.push('/');
      } else {
        colors.push('rgba(10, 119, 255, 1)');
        patterns.push('');
      }
    });
  }

  chartData[0].x = xLabels;
  chartData[0].y = yValues;
  chartData[0].marker.color = colors;
  chartData[0].marker.pattern.shape = patterns;

  let dynamicTickVals = [];
  let dynamicTickText = [];
  let dynamicRange = [0, 110];

  if (yValues.length > 0) {
    const yMax = Math.max(0, ...yValues);
    const yMin = 0;

    let tickStep = 25;

    if (type === 'count') {
      if (yMax <= 10) {
        tickStep = 1;
      } else if (yMax <= 50) {
        tickStep = 5;
      } else if (yMax <= 100) {
        tickStep = 10;
      } else {
        const roughStep = yMax / 5;
        const powersOf10 = Math.pow(10, Math.floor(Math.log10(roughStep)));
        tickStep = Math.ceil(roughStep / powersOf10) * powersOf10;
        if (tickStep < 1) tickStep = 1;
        tickStep = Math.ceil(tickStep / 5) * 5;
      }
    } else { // 'currency'
      if (yMax <= 100) {
        tickStep = 10;
      } else if (yMax <= 500) {
        tickStep = 50;
      } else if (yMax <= 1000) {
        tickStep = 100;
      } else if (yMax <= 5000) {
        tickStep = 500;
      } else if (yMax <= 10000) {
        tickStep = 1000;
      } else if (yMax <= 50000) {
        tickStep = 5000;
      } else if (yMax <= 100000) {
        tickStep = 10000;
      } else if (yMax <= 500000) {
        tickStep = 50000;
      } else {
        const roughStep = yMax / 5;
        const powersOf10 = Math.pow(10, Math.floor(Math.log10(roughStep)));
        tickStep = Math.ceil(roughStep / powersOf10) * powersOf10;
        if (tickStep < 10) tickStep = 10;
        if (tickStep % 50 !== 0 && tickStep < 500) {
          tickStep = Math.ceil(tickStep / 50) * 50;
        } else if (tickStep % 100 !== 0 && tickStep < 1000) {
          tickStep = Math.ceil(tickStep / 100) * 100;
        } else if (tickStep % 1000 !== 0) {
          tickStep = Math.ceil(tickStep / 1000) * 1000;
        }
      }
    }

    const effectiveYMaxForTicks = Math.ceil(yMax / tickStep) * tickStep;

    for (let i = yMin; i <= effectiveYMaxForTicks; i += tickStep) {
      dynamicTickVals.push(i);
      dynamicTickText.push(type === 'currency' ? `${formatValueWithPrefix(i)}₴` : `${formatValueWithPrefix(i)}`);
    }

    const rangePadding = tickStep * 0.2;
    dynamicRange = [yMin, effectiveYMaxForTicks + rangePadding];

    if (dynamicTickVals.length > 0 && dynamicTickVals[0] === 0) {
      dynamicTickText[0] = type === 'currency' ? '0₴' : '0';
    }
  }

  const overrides = {
    layout: {
      dragmode: false,
      hovermode: false,
      scrollZoom: false,
      font: {
        family: 'Geist, sans-serif',
        size: 14,
        color: 'rgba(107, 114, 128, 1)',
      },
      margin: { l: 10, r: 50, b: 50, t: 10 },
      showlegend: false,
      plot_bgcolor: 'white',
      paper_bgcolor: 'white',
      uniformtext: { minsize: 9, mode: 'hide' },
      bargap: 0.5,
      xaxis: {
        tickfont: {
          family: 'Geist, sans-serif',
          size: 14,
          color: 'rgba(107, 114, 128, 1)',
        },
        showticklabels: true,
        automargin: true,
        fixedrange: true,
      },
      yaxis: {
        title: '',
        showgrid: true,
        gridcolor: 'rgba(229, 229, 231, 1)',
        griddash: 'dash',
        tickprefix: '',
        tickvals: dynamicTickVals,
        tickmode: 'array',
        ticktext: dynamicTickText,
        range: dynamicRange,
        zeroline: true,
        zerolinecolor: 'rgba(229, 229, 231, 1)',
        tickfont: {
          family: 'Geist, sans-serif',
          size: 14,
          color: 'rgba(107, 114, 128, 1)',
        },
        fixedrange: true,
      },
    },
    data: chartData,
  };

  return chartOptionsFactory.createChartOptions(overrides);
}
